import { api } from './client'
import type {
    ApiEnvelope,
    MailTemplate,
    PaginatedResponse,
    MailTemplatePlaceholder,
    MailTemplateFunction
} from '@/types/api'

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export interface MailTemplateQueryParams {
    name?: string
    title?: string
    functionId?: number
    unitId?: string
    status?: number
    fromDate?: string
    toDate?: string
}

export interface MailTemplateWriteInput {
    unitId: string
    name: string
    functionId: number
    title: string
    content?: string
    fileIds?: string[]
    status?: number
}

export interface SendTestMailInput {
    unitId?: string
    name: string
    functionId: number
    title: string
    content?: string
    fileIds?: string[]
    receiveMail?: string
    mailServerId?: string
}

const MAIL_TEMPLATE_BASE = '/v1/robot/mail-template-management'

function adaptPage<T>(
    raw: SpringPage<T> | PaginatedResponse<T> | T[] | null | undefined,
): PaginatedResponse<T> {
    if (!raw) return { items: [], total: 0, page: 1, limit: 10, totalPages: 0 }
    if (Array.isArray(raw)) return { items: raw, total: raw.length, page: 1, limit: Math.max(10, raw.length), totalPages: raw.length ? 1 : 0 }
    if ('items' in raw) return raw
    return {
        items: raw.content,
        total: raw.totalElements || 0,
        page: (raw.number || 0) + 1,
        limit: raw.size || 10,
        totalPages: raw.totalPages || 0,
    }
}

function adaptMailTemplatePage(
    raw: SpringPage<MailTemplate> | PaginatedResponse<MailTemplate> | MailTemplate[] | null | undefined,
): PaginatedResponse<MailTemplate> {
    return adaptPage(raw)
}

function toQueryParams(params: MailTemplateQueryParams = {}) {
    return {
        name: params.name?.trim() || undefined,
        title: params.title?.trim() || undefined,
        functionId: params.functionId,
        unitId: params.unitId || undefined,
        status: params.status,
        fromDate: params.fromDate,
        toDate: params.toDate,
    }
}

function getErrorMessage(error: any, fallback: string) {
    return error?.response?.data?.responseMessage || error?.message || fallback
}

export async function fetchMailTemplates(
    page = 0,
    pageSize = 10,
    params: MailTemplateQueryParams = {},
    signal?: AbortSignal,
): Promise<PaginatedResponse<MailTemplate>> {
    try {
        const response = await api.get<ApiEnvelope<SpringPage<MailTemplate> | MailTemplate[]>>(
            MAIL_TEMPLATE_BASE,
            {
                params: { page, size: pageSize, ...toQueryParams(params) },
                signal,
            },
        )
        return adaptMailTemplatePage(response.data.responseData)
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi lấy danh sách mẫu mail.'))
    }
}

export async function getMailTemplateById(id: string, signal?: AbortSignal): Promise<MailTemplate> {
    try {
        const response = await api.get<ApiEnvelope<MailTemplate>>(`${MAIL_TEMPLATE_BASE}/${id}`, { signal })
        return response.data.responseData
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi lấy chi tiết mẫu mail.'))
    }
}

export async function createMailTemplate(data: MailTemplateWriteInput): Promise<ApiEnvelope<MailTemplate>> {
    try {
        const payload = {
            ...data,
            name: data.name.trim(),
            title: data.title.trim(),
        }
        const response = await api.post<ApiEnvelope<MailTemplate>>(MAIL_TEMPLATE_BASE, payload)
        return response.data
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi tạo mẫu mail.'))
    }
}

export async function updateMailTemplate(id: string, data: MailTemplateWriteInput): Promise<ApiEnvelope<MailTemplate>> {
    try {
        const payload = {
            ...data,
            name: data.name.trim(),
            title: data.title.trim(),
        }
        const response = await api.put<ApiEnvelope<MailTemplate>>(`${MAIL_TEMPLATE_BASE}/${id}`, payload)
        return response.data
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi cập nhật mẫu mail.'))
    }
}

export async function updateMailTemplateStatus(template: MailTemplate, newStatus: number): Promise<ApiEnvelope<MailTemplate>> {
    const fullTemplate = await getMailTemplateById(template.id)
    return updateMailTemplate(template.id, {
        unitId: fullTemplate.unitId,
        name: fullTemplate.name,
        functionId: fullTemplate.functionId,
        title: fullTemplate.title,
        content: fullTemplate.content,
        fileIds: fullTemplate.files?.map((f: any) => f.id),
        status: newStatus,
    })
}

export async function deleteMailTemplate(id: string): Promise<ApiEnvelope<unknown>> {
    try {
        const response = await api.delete<ApiEnvelope<unknown>>(`${MAIL_TEMPLATE_BASE}/${id}`)
        return response.data
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi xóa mẫu mail.'))
    }
}

export async function sendTestMailTemplate(data: SendTestMailInput): Promise<ApiEnvelope<unknown>> {
    try {
        const response = await api.post<ApiEnvelope<unknown>>(`${MAIL_TEMPLATE_BASE}/send-test-mail`, data)
        return response.data
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi gửi thử mail.'))
    }
}

export async function getMailTemplatePlaceholders(functionId?: number): Promise<MailTemplatePlaceholder[]> {
    try {
        const response = await api.get<ApiEnvelope<MailTemplatePlaceholder[]>>(`${MAIL_TEMPLATE_BASE}/placeholders`, {
            params: { functionId }
        })
        return response.data.responseData
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi lấy danh sách placeholder.'))
    }
}

export async function getMailTemplateFunctions(): Promise<MailTemplateFunction[]> {
    try {
        const response = await api.get<ApiEnvelope<MailTemplateFunction[]>>(`${MAIL_TEMPLATE_BASE}/functions`)
        return response.data.responseData
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi lấy danh sách chức năng.'))
    }
}